/**
 * Хук жизненного цикла keycloak-js для survey-client: login-required, tokenUpdate, авто-обновление.
 * Структура и паттерн tokenUpdate совпадают с passport-client `useKeycloak`.
 *
 * @module hooks/keycloak/keycloak.hook
 */

import { decodeJwt } from 'jose';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { AuthInitStatus } from '../../context/authInit.types';
import { ROUTE_REGISTRATION_SURVEY } from '../../routes/routes.constants';
import { cleanUrlAfterKeycloakAuth } from '../../utils/keycloakAuthUrl';
import { prepareBusinessUnitRedirectFromWindow } from '../../utils/surveyRedirectTarget';

import { initKeycloakCheckSso, keycloakInit } from './keycloak.functions';
import type { IUseKeycloak, TKProfile } from './keycloak.types';
import { resolvePostLogoutRedirectUri } from './keycloakLogoutRedirect';

/**
 * Инициализация Keycloak (login-required), получение токена через PKCE, авто-обновление токена.
 * При отсутствии сессии — keycloak-js автоматически перенаправляет на страницу входа Keycloak.
 *
 * @param params - Параметры
 * @param params.mocked - Использовать KeycloakMock (Storybook / тесты)
 * @returns {IUseKeycloak} токен, профиль, kLogin/kLogout/kRefresh и статус первичного init
 */
function useKeycloak({ mocked }: { mocked: boolean }): IUseKeycloak {
  const keycloak = useMemo(() => keycloakInit(mocked), [mocked]);

  const [initStatus, setInitStatus] = useState<AuthInitStatus>('pending');
  const [initError, setInitError] = useState<Error | null>(null);
  const [kToken, setKToken] = useState<string | null | boolean>(null);
  const [kProfile, setKProfile] = useState<TKProfile | null>(null);

  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoLoginStartedRef = useRef(false);

  const tokenUpdate = useCallback(
    (status: boolean) => {
      if (refreshTimerRef.current !== null) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }

      const token: boolean | string = status ? keycloak.token || false : false;
      setKToken(token);

      if (typeof token === 'string') {
        const jwtToken = decodeJwt(token) as TKProfile;
        setKProfile(jwtToken);
        const secondsUntilExpiry = jwtToken.exp - Math.floor(Date.now() / 1000);

        refreshTimerRef.current = setTimeout(() => {
          void keycloak
            .updateToken(-1)
            .then((kRefreshed: boolean) => {
              tokenUpdate(kRefreshed);
            })
            .catch((error_: unknown) => {
              tokenUpdate(false);
              const message =
                error_ instanceof Error ? error_.message : 'Failed to refresh the token, or the session has expired';
              console.error(message);
            });
        }, secondsUntilExpiry * 1000);
      } else {
        console.warn('Keycloak hook: typeof token is not a string');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [keycloak],
  );

  const kLogin = useCallback(async () => {
    // Важно: Keycloak должен редиректить строго в `VITE_APP_URL` (локально это всегда `11122`),
    // а не в `window.location.origin`, который может быть подменён внешним редиректом.
    const callbackOrigin = import.meta.env.VITE_APP_URL || window.location.origin;
    const callbackUrl = new URL(ROUTE_REGISTRATION_SURVEY, callbackOrigin);
    const currentSearchParams = new URLSearchParams(window.location.search);
    const cmpImport = currentSearchParams.get('cmp_import') || currentSearchParams.get('cmpimport');
    callbackUrl.searchParams.set('redirect_uri', prepareBusinessUnitRedirectFromWindow());
    if (typeof cmpImport === 'string' && cmpImport !== '') {
      callbackUrl.searchParams.set('cmp_import', cmpImport);
    }

    await keycloak.login({ redirectUri: callbackUrl.toString() });
  }, [keycloak]);

  useEffect(() => {
    let active = true;
    initKeycloakCheckSso(keycloak)
      .then((authenticated) => {
        if (!active) {
          return;
        }
        tokenUpdate(authenticated);
        if (authenticated) {
          cleanUrlAfterKeycloakAuth();
        }
        if (authenticated === false && !autoLoginStartedRef.current) {
          autoLoginStartedRef.current = true;
          void kLogin();
        }
        setInitStatus('ready');
        setInitError(null);
      })
      .catch((error_: unknown) => {
        if (!active) {
          return;
        }
        const error = error_ instanceof Error ? error_ : new Error('Keycloak init failed');
        setInitError(error);
        setInitStatus('error');
        tokenUpdate(false);
        console.error('Keycloak hook: failed to initialize in Keycloak');
      });
    return () => {
      active = false;
    };
  }, [keycloak, tokenUpdate, kLogin]);

  const kLogout = useCallback(
    async (redirectUri?: string) => {
      keycloak.idToken = '';
      const target = resolvePostLogoutRedirectUri(redirectUri);
      await keycloak.logout({ redirectUri: target });
      setKToken(null);
      setKProfile(null);
    },
    [keycloak],
  );

  const kRefresh = useCallback(async () => {
    setKProfile(null);
    await keycloak
      .updateToken(-1)
      .then((kRefreshed: boolean) => {
        tokenUpdate(kRefreshed);
      })
      .catch((error_: unknown) => {
        tokenUpdate(false);
        const message =
          error_ instanceof Error ? error_.message : 'Failed to refresh the token, or the session has expired';
        console.error(message);
      });
  }, [keycloak, tokenUpdate]);

  return {
    initStatus,
    initError,
    kToken,
    kProfile,
    kLogin,
    kLogout,
    kRefresh,
  };
}

export default useKeycloak;
